import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Plus, Pencil, Trash2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { NAV_CATEGORIES, CATEGORY_META } from "@/i18n/translations";

const STATUS_COLORS = {
  published: "bg-green-50 text-green-700 border-green-200",
  draft: "bg-sand text-anthracite/60 border-tertiary",
  archived: "bg-red-50 text-red-600 border-red-200",
};

export default function ListingsAdmin() {
  const [params] = useSearchParams();
  const [listings, setListings] = useState([]);
  const [category, setCategory] = useState(params.get("category") || "");
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/admin/listings", { params: category ? { category } : {} })
      .then(({ data }) => setListings(data)).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, [category]);

  const del = async (id) => {
    if (!window.confirm("Supprimer cette fiche ?")) return;
    await api.delete(`/admin/listings/${id}`);
    toast.success("Fiche supprimée");
    load();
  };

  return (
    <div>
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="font-serif text-3xl text-anthracite">Fiches</h1>
          <p className="text-sm text-anthracite/50">Opportunités, projets, partenaires, marchés…</p>
        </div>
        <Link to="/admin/listings/new" data-testid="new-listing-btn" className="inline-flex items-center gap-2 bg-anthracite px-5 py-2.5 text-sm font-medium text-ivory transition-colors hover:bg-black">
          <Plus className="h-4 w-4" /> Nouvelle fiche
        </Link>
      </div>

      <div className="mb-6 flex flex-wrap gap-2">
        <button onClick={() => setCategory("")} className={`rounded-full border px-4 py-1.5 text-xs ${!category ? "border-anthracite bg-anthracite text-ivory" : "border-tertiary bg-white text-anthracite/60"}`}>Tout</button>
        {NAV_CATEGORIES.map((c) => (
          <button key={c} onClick={() => setCategory(c)} className={`rounded-full border px-4 py-1.5 text-xs ${category === c ? "border-anthracite bg-anthracite text-ivory" : "border-tertiary bg-white text-anthracite/60"}`}>
            {CATEGORY_META[c].fr}
          </button>
        ))}
      </div>

      <div className="overflow-hidden border border-tertiary bg-white">
        <table className="w-full text-sm">
          <thead className="border-b border-tertiary bg-sand text-left text-xs uppercase tracking-wider text-anthracite/50">
            <tr>
              <th className="px-5 py-3">Titre</th>
              <th className="px-5 py-3">Domaine</th>
              <th className="px-5 py-3">Statut</th>
              <th className="px-5 py-3">Vérif.</th>
              <th className="px-5 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="px-5 py-12 text-center text-anthracite/40">…</td></tr>
            ) : listings.length === 0 ? (
              <tr><td colSpan={5} className="px-5 py-12 text-center text-anthracite/40" data-testid="listings-empty">Aucune fiche. Créez la première.</td></tr>
            ) : (
              listings.map((l) => (
                <tr key={l.id} data-testid={`listing-row-${l.id}`} className="border-b border-tertiary last:border-0 hover:bg-sand/50">
                  <td className="px-5 py-3 font-medium text-anthracite">{l.title_fr || l.title_en || "(sans titre)"}</td>
                  <td className="px-5 py-3 text-anthracite/60">{CATEGORY_META[l.category]?.fr || l.category}</td>
                  <td className="px-5 py-3">
                    <span className={`rounded-full border px-2.5 py-0.5 text-xs ${STATUS_COLORS[l.status] || ""}`}>{l.status}</span>
                  </td>
                  <td className="px-5 py-3">
                    {l.verification?.level && l.verification.level !== "unverified" ? (
                      <span className="inline-flex items-center gap-1 text-xs text-champagne"><ShieldCheck className="h-3.5 w-3.5" />{l.verification.level}</span>
                    ) : <span className="text-xs text-anthracite/30">—</span>}
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <Link to={`/admin/listings/${l.id}/edit`} data-testid={`edit-listing-${l.id}`} className="rounded p-1.5 text-anthracite/60 hover:bg-sand hover:text-champagne"><Pencil className="h-4 w-4" /></Link>
                      <button onClick={() => del(l.id)} data-testid={`delete-listing-${l.id}`} className="rounded p-1.5 text-anthracite/60 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
